/**
 * betting.ts —— 下注轮规则：合法动作判定与动作应用（PRD 1.1 / 1.2、AC1.2 / AC1.6）
 *
 * 设计取舍说明：
 *
 * 1. **金额一律用「加注到」（to）语义，不用「增量」语义。**
 *    bet / raise 的 amount 表示「本街累计投入达到多少」，而不是「再放多少筹码」。
 *    理由：增量语义在「跟注 + 加注」混合时极易出现口径歧义（玩家输入 100 到底是
 *    加到 100 还是在跟注之外再加 100），业界客户端也普遍在协议层用 to 语义。
 *    call 的 amount 则是「现在需要放入的筹码数」，因为跟注没有歧义。
 *
 * 2. **最小加注 = currentBet + lastRaiseSize，且 lastRaiseSize 只在「足额」加注时更新。**
 *    例（盲注 10/20）：A 加到 60（增量 40）→ B 全下 80（增量 20，不足额）→
 *    C 的最小加注为 80 + 40 = 120。这是 TDA 与主流房间的口径：不足额全下不改变
 *    最小加注增量。此处显式记录，避免后人误改为 80 + 20。
 *
 * 3. **不足额全下不重开下注轮（PRD 1.2）。**
 *    实现方式：发生不足额全下时，把「本街已行动过」的玩家的加注权撤销
 *    （raiseRightRevoked = true），他们只能跟注差额或弃牌。尚未行动的玩家、
 *    以及此前只在更低水位行动过的玩家不受影响，仍可加注 —— 后者由「足额加注会重置
 *    hasActedThisStreet」这一机制自动保证。
 */

/** 玩家动作类型。 */
export type ActionType = 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'allin';

/** 一个玩家动作。 */
export interface Action {
  readonly type: ActionType;
  /**
   * bet / raise 时表示「本街累计投入要达到的总额」（to 语义）。
   * fold / check / call / allin 不需要，传入会被忽略。
   */
  readonly amount?: number;
}

/** 下注轮中单个玩家的可变状态。 */
export interface BettingPlayer {
  readonly seat: number;
  /** 剩余筹码。 */
  stack: number;
  /** 本街累计投入。 */
  streetContributed: number;
  /** 本手累计投入（含盲注 / 前注），用于边池分层。 */
  handContributed: number;
  folded: boolean;
  allIn: boolean;
  /** 本街是否已经行动过（盲注 / 前注的被动投入不算行动）。 */
  hasActedThisStreet: boolean;
  /** 加注权是否已被「不足额全下」撤销（PRD 1.2）。 */
  raiseRightRevoked: boolean;
}

/** 下注轮上下文。game.ts 持有权威状态，这里只做纯计算。 */
export interface BettingContext {
  /** 按座位号索引，可能有空位（已淘汰 / 未入座）。 */
  readonly players: readonly (BettingPlayer | null)[];
  /** 本街最高累计投入额。翻牌前初始为大盲额。 */
  currentBet: number;
  /** 最近一次**足额**下注 / 加注的增量。翻牌前初始为大盲额。 */
  lastRaiseSize: number;
  /** 大盲金额，决定翻牌后的最小下注额。 */
  readonly bigBlind: number;
}

/** 某玩家当前可执行的动作集合。 */
export interface LegalActions {
  readonly fold: boolean;
  readonly check: boolean;
  /** 跟注：amount 为现在需要放入的筹码数；allIn 表示筹码不足以补齐、跟注即全下。 */
  readonly call: { readonly amount: number; readonly allIn: boolean } | null;
  /** 主动下注（本街尚无人下注时）。min / max 为 to 语义。 */
  readonly bet: { readonly min: number; readonly max: number } | null;
  /** 加注（本街已有下注时）。min / max 为 to 语义。 */
  readonly raise: { readonly min: number; readonly max: number } | null;
  /** 全下：to 为本街累计将达到的总额，chips 为放入的筹码数。 */
  readonly allIn: { readonly to: number; readonly chips: number } | null;
  /** 需要补齐的差额（= currentBet − streetContributed，下限 0）。 */
  readonly toCall: number;
}

function requirePlayer(ctx: BettingContext, seat: number): BettingPlayer {
  const p = ctx.players[seat];
  if (!p) throw new Error(`座位 ${seat} 上没有玩家`);
  return p;
}

/**
 * 计算某座位的合法动作集合。
 *
 * 注意：本函数**不**判断「是否轮到该玩家行动」，那是状态机（game.ts）的职责。
 */
export function legalActions(ctx: BettingContext, seat: number): LegalActions {
  const p = requirePlayer(ctx, seat);

  if (p.folded || p.allIn || p.stack <= 0) {
    return { fold: false, check: false, call: null, bet: null, raise: null, allIn: null, toCall: 0 };
  }

  const toCall = Math.max(0, ctx.currentBet - p.streetContributed);
  const maxTo = p.streetContributed + p.stack; // 全下后本街累计投入

  // 过牌：不需要补差额时才可以
  const canCheck = toCall === 0;

  // 跟注：需要补差额时才有意义；筹码不足则为「跟注即全下」
  const call = toCall > 0 ? { amount: Math.min(toCall, p.stack), allIn: p.stack <= toCall } : null;

  // 主动下注：本街还没有人下注
  let bet: LegalActions['bet'] = null;
  if (ctx.currentBet === 0) {
    const min = Math.min(ctx.bigBlind, maxTo); // 筹码不足一个大盲时，全下即最小下注
    if (maxTo > 0) bet = { min, max: maxTo };
  }

  // 加注：本街已有下注，且未被撤销加注权，且筹码能超过当前注额
  let raise: LegalActions['raise'] = null;
  if (ctx.currentBet > 0 && !p.raiseRightRevoked && maxTo > ctx.currentBet) {
    const wantMin = ctx.currentBet + ctx.lastRaiseSize;
    // 筹码不足足额加注时，只允许「不足额全下」这一个金额
    const min = Math.min(wantMin, maxTo);
    raise = { min, max: maxTo };
  }

  const allIn = p.stack > 0 ? { to: maxTo, chips: p.stack } : null;

  return { fold: true, check: canCheck, call, bet, raise, allIn, toCall };
}

/** 动作校验失败的结构化错误（AC1.2 要求「给出原因」）。 */
export class IllegalActionError extends Error {
  readonly seat: number;
  readonly action: Action;
  /** 机器可读的原因码，便于 UI 文案映射与埋点。 */
  readonly code: string;
  constructor(seat: number, action: Action, code: string, message: string) {
    super(message);
    this.name = 'IllegalActionError';
    this.seat = seat;
    this.action = action;
    this.code = code;
  }
}

/** 动作应用后的效果描述，供状态机记录事件与推进流程。 */
export interface ActionEffect {
  readonly seat: number;
  /** 归一化后的动作类型：allin 会被归一为 call / bet / raise 之一，便于统计 AF。 */
  readonly type: ActionType;
  /** 原始动作类型（玩家 / AI 实际提交的）。 */
  readonly rawType: ActionType;
  /** 本次放入的筹码数。 */
  readonly chips: number;
  /** 动作后该玩家本街累计投入。 */
  readonly to: number;
  readonly allIn: boolean;
  /** 本动作是否为「足额」下注 / 加注（决定是否重开下注轮）。 */
  readonly fullRaise: boolean;
  /** 本动作是否重开了下注轮（其他玩家重获加注权）。 */
  readonly reopened: boolean;
}

/**
 * 校验并应用一个动作，就地修改 ctx。
 *
 * @throws IllegalActionError 动作非法时抛出，且**不修改任何状态**（先校验后修改）。
 */
export function applyAction(ctx: BettingContext, seat: number, action: Action): ActionEffect {
  const p = requirePlayer(ctx, seat);
  const legal = legalActions(ctx, seat);

  if (p.folded) throw new IllegalActionError(seat, action, 'ALREADY_FOLDED', `座位 ${seat} 已弃牌，不能再行动`);
  if (p.allIn) throw new IllegalActionError(seat, action, 'ALREADY_ALLIN', `座位 ${seat} 已全下，不能再行动`);

  const rawType = action.type;

  // ---- 1. 归一化：把 allin 翻译成语义等价的 call / bet / raise ----
  let type = rawType;
  let to: number;

  switch (rawType) {
    case 'fold': {
      p.folded = true;
      p.hasActedThisStreet = true;
      return { seat, type: 'fold', rawType, chips: 0, to: p.streetContributed, allIn: false, fullRaise: false, reopened: false };
    }

    case 'check': {
      if (!legal.check) {
        throw new IllegalActionError(seat, action, 'CANNOT_CHECK', `当前需跟注 ${legal.toCall}，不能过牌`);
      }
      p.hasActedThisStreet = true;
      return { seat, type: 'check', rawType, chips: 0, to: p.streetContributed, allIn: false, fullRaise: false, reopened: false };
    }

    case 'call': {
      if (!legal.call) throw new IllegalActionError(seat, action, 'NOTHING_TO_CALL', '当前无需跟注，请使用过牌');
      to = p.streetContributed + legal.call.amount;
      break;
    }

    case 'allin': {
      if (!legal.allIn) throw new IllegalActionError(seat, action, 'NO_CHIPS', `座位 ${seat} 没有可用筹码`);
      to = legal.allIn.to;
      // 全下的语义归一：超过当前注额算下注/加注，否则算跟注
      if (to > ctx.currentBet) type = ctx.currentBet === 0 ? 'bet' : 'raise';
      else type = ctx.currentBet === 0 ? 'bet' : 'call';
      break;
    }

    case 'bet': {
      if (!legal.bet) {
        throw new IllegalActionError(seat, action, 'CANNOT_BET', `本街已有下注 ${ctx.currentBet}，请使用加注`);
      }
      const amt = action.amount;
      if (amt === undefined || !Number.isInteger(amt)) {
        throw new IllegalActionError(seat, action, 'BAD_AMOUNT', '下注额必须为整数');
      }
      if (amt < legal.bet.min) {
        throw new IllegalActionError(seat, action, 'BET_TOO_SMALL', `下注额 ${amt} 低于最小下注额 ${legal.bet.min}`);
      }
      if (amt > legal.bet.max) {
        throw new IllegalActionError(seat, action, 'BET_TOO_LARGE', `下注额 ${amt} 超过可用筹码上限 ${legal.bet.max}`);
      }
      to = amt;
      break;
    }

    case 'raise': {
      if (!legal.raise) {
        if (ctx.currentBet === 0) {
          throw new IllegalActionError(seat, action, 'CANNOT_RAISE', '本街尚无下注，请使用下注');
        }
        if (p.raiseRightRevoked) {
          throw new IllegalActionError(
            seat,
            action,
            'RAISE_RIGHT_REVOKED',
            '上一个全下额不足最小加注，本轮未重开，你只能跟注或弃牌',
          );
        }
        throw new IllegalActionError(seat, action, 'CANNOT_RAISE', `筹码不足以加注至超过 ${ctx.currentBet}`);
      }
      const amt = action.amount;
      if (amt === undefined || !Number.isInteger(amt)) {
        throw new IllegalActionError(seat, action, 'BAD_AMOUNT', '加注额必须为整数');
      }
      if (amt < legal.raise.min) {
        throw new IllegalActionError(seat, action, 'RAISE_TOO_SMALL', `加注至 ${amt} 低于最小加注额 ${legal.raise.min}`);
      }
      if (amt > legal.raise.max) {
        throw new IllegalActionError(seat, action, 'RAISE_TOO_LARGE', `加注至 ${amt} 超过可用筹码上限 ${legal.raise.max}`);
      }
      to = amt;
      break;
    }

    default:
      throw new IllegalActionError(seat, action, 'UNKNOWN_ACTION', `未知动作类型：${String(rawType)}`);
  }

  // ---- 2. 扣筹码 ----
  const chips = to - p.streetContributed;
  if (chips < 0) throw new IllegalActionError(seat, action, 'NEGATIVE_CHIPS', '动作会导致投入减少，逻辑错误');
  if (chips > p.stack) {
    throw new IllegalActionError(seat, action, 'INSUFFICIENT_CHIPS', `需要 ${chips} 筹码，仅剩 ${p.stack}`);
  }

  p.stack -= chips;
  p.streetContributed = to;
  p.handContributed += chips;
  p.hasActedThisStreet = true;
  if (p.stack === 0) p.allIn = true;

  // ---- 3. 更新注额水位 / 加注权 ----
  let fullRaise = false;
  let reopened = false;

  if (to > ctx.currentBet) {
    const increment = to - ctx.currentBet;
    // 足额判定：增量 ≥ 上一次足额加注增量（首次下注则与大盲比）
    const requiredIncrement = ctx.currentBet === 0 ? ctx.bigBlind : ctx.lastRaiseSize;
    fullRaise = increment >= requiredIncrement;

    ctx.currentBet = to;
    if (fullRaise) {
      ctx.lastRaiseSize = increment;
      reopened = true;
      // 足额加注：其余在场玩家全部重获加注权与行动权
      for (const q of ctx.players) {
        if (!q || q.seat === seat || q.folded || q.allIn) continue;
        q.raiseRightRevoked = false;
        q.hasActedThisStreet = false;
      }
    } else {
      // 不足额全下：不重开下注轮（PRD 1.2 / TDA）
      // 「本街已行动过」的玩家失去加注权，只能跟注差额或弃牌；但仍需给他们行动机会。
      //
      // 为什么条件就是 hasActedThisStreet：足额加注会把其余玩家的 hasActedThisStreet
      // 重置为 false（见上方分支），所以该标记为 true 恰好等价于「已在**当前**注额水位
      // 行动过」。此前只在更低水位行动过的玩家，其标记早已被那次足额加注清掉，
      // 因此不会被误撤销加注权，符合 TDA「面对足额加注者保留加注权」的口径。
      //
      // 注意：不要再加 `q.streetContributed >= to` 这类条件 —— 本分支内恒有
      // to > ctx.currentBet >= q.streetContributed，该条件永远为 false，会让整条规则变成死代码。
      for (const q of ctx.players) {
        if (!q || q.seat === seat || q.folded || q.allIn) continue;
        if (q.hasActedThisStreet) {
          q.raiseRightRevoked = true;
        }
        // 只要还没补齐差额，就需要再次行动（跟注或弃牌）
        if (q.streetContributed < to) q.hasActedThisStreet = false;
      }
    }
  }

  return { seat, type, rawType, chips, to, allIn: p.allIn, fullRaise, reopened };
}

/**
 * 下注轮是否已结束。
 *
 * 结束条件：所有「未弃牌且未全下」的玩家都已行动过，且本街投入都等于 currentBet。
 * 另有一个提前结束条件：未弃牌玩家中至多 1 人还有筹码可下注（其余全下或弃牌）
 * 且该玩家已补齐差额 —— 此时无人可再行动。
 */
export function isBettingRoundComplete(ctx: BettingContext): boolean {
  const actionable = ctx.players.filter((p): p is BettingPlayer => !!p && !p.folded && !p.allIn && p.stack > 0);
  if (actionable.length === 0) return true;

  if (actionable.length === 1) {
    /*
     * 只剩 1 名玩家还有筹码可动（其余玩家已弃牌或全下）——此时**不存在下注轮**：
     * 没有对手能跟他的注，下注在规则上没有意义。只要他已补齐差额（或本就无差额），
     * 本轮即结束，后续公共牌直接发完进入比牌。
     *
     * 这条分支是必须的：早期实现只判断 actionable.length === 0，导致
     * 「对手盲注全下、我是唯一有筹码的玩家」时引擎会在每条街都问我一次动作，
     * 于是可以在翻牌圈弃掉一手已经无需再投入任何筹码的牌，进而产生
     * 「最高投入者已弃牌」的边池，破坏筹码守恒。已由 fuzz 用例锁定。
     */
    return actionable[0].streetContributed >= ctx.currentBet;
  }

  for (const p of actionable) {
    if (!p.hasActedThisStreet) return false;
    if (p.streetContributed < ctx.currentBet) return false;
  }
  return true;
}

/** 找出下一个需要行动的座位（从 fromSeat 之后顺时针查找）。返回 null 表示本轮结束。 */
export function nextToAct(ctx: BettingContext, fromSeat: number): number | null {
  const n = ctx.players.length;
  for (let i = 1; i <= n; i++) {
    const seat = (fromSeat + i) % n;
    const p = ctx.players[seat];
    if (!p || p.folded || p.allIn || p.stack <= 0) continue;
    if (!p.hasActedThisStreet || p.streetContributed < ctx.currentBet) return seat;
  }
  return null;
}

/** 进入新一街：重置本街投入与行动标记。 */
export function resetForNewStreet(ctx: BettingContext): void {
  ctx.currentBet = 0;
  ctx.lastRaiseSize = ctx.bigBlind; // 翻牌后最小下注 / 加注增量以大盲为基准
  for (const p of ctx.players) {
    if (!p) continue;
    p.streetContributed = 0;
    p.hasActedThisStreet = false;
    p.raiseRightRevoked = false;
  }
}
